import React, { useEffect, useState } from "react";
import Icon from "../../../Components/icons";

import type2Select from "../../../Assets/Image/radio-button-type-2-selected.svg";
import type2UnSelect from "../../../Assets/Image/radio-button-typ-2-unselected.svg";
import { UpiVerifyModal, SuccessModal } from "../../../Components/Modals";
import { Input, SelectInput } from "../../../Components/FormElements";
import { PrimaryButton } from "../../../Components/Cta";

export default function Stage4({ setTitle, updateData, changePage }) {
  const [selected, setSelected] = useState("manual"); // null | "upi" | "manual"
  const [otpModal, setOtpModal] = useState(false);
  const [upiValue, setUpiValue] = useState("");
  const [upiError, setUpiError] = useState(false);
  const [upiVerified, setUpiVerified] = useState(false);

  // ✅ Separate states for manual entry
  const [accountType, setAccountType] = useState("");
  const [accountNumber, setAccountNumber] = useState("");
  const [ifsc, setIfsc] = useState("");
  const [holderName, setHolderName] = useState("");

  // ✅ Separate error states
  const [accountTypeError, setAccountTypeError] = useState("");
  const [accountNumberError, setAccountNumberError] = useState("");
  const [ifscError, setIfscError] = useState("");
  const [holderNameError, setHolderNameError] = useState("");

  useEffect(() => {
    if (selected === "manual") {
      setTitle("Manually verify via bank");
    } else {
      setTitle("Verify & link your bank");
    }
  }, [selected, setTitle]);

  const handleSelect = (option) => {
    setSelected(option);
    if (option === "upi") {
      setOtpModal(true);
    } else {
      setOtpModal(false);
    }
  };

  // ✅ Validation for UPI
  const validateUpi = (upi) => {
    const upiRegex = /^[a-zA-Z0-9.\-_]{2,256}@[a-zA-Z]{2,64}$/;
    return upiRegex.test(upi.trim());
  };

  const handleUpiSubmit = () => {
    if (!validateUpi(upiValue)) {
      setUpiError(true);
      return;
    }
    setUpiError(false);
    // console.log("UPI Verified:", upiValue);
    setOtpModal(false);
    setUpiVerified(true);
  };

  // ✅ Manual form validation
  const validateForm = () => {
    let valid = true;

    if (!accountType) {
      setAccountTypeError("Please select account type");
      valid = false;
    } else {
      setAccountTypeError("");
    }

    if (!accountNumber) {
      setAccountNumberError("Account number is required");
      valid = false;
    } else if (!/^\d{9,18}$/.test(accountNumber)) {
      setAccountNumberError("Enter a valid account number (9–18 digits)");
      valid = false;
    } else {
      setAccountNumberError("");
    }

    if (!ifsc) {
      setIfscError("IFSC Code is required");
      valid = false;
    } else if (!/^[A-Z]{4}0[A-Z0-9]{6}$/.test(ifsc.trim().toUpperCase())) {
      setIfscError("Enter a valid IFSC code");
      valid = false;
    } else {
      setIfscError("");
    }

    if (!holderName) {
      setHolderNameError("Account holder name is required");
      valid = false;
    } else {
      setHolderNameError("");
    }

    return valid;
  };

  const _handleSubmit = () => {
    if (validateForm()) {
      const formData = {
        accountType,
        accountNumber,
        ifsc,
        holderName,
      };

      // console.log("Manual form submitted:", formData);
      updateData((prev) => ({ ...prev, ...formData }));
      changePage(5);
    }
  };

  return (
    <>
      {selected === "manual" ? (
        <div className="p-3 d-flex flex-column row-gap-3 mt-1 e-bg-ghost-white-dark e-border-radius-16 e-border-1px-pale-lavender">
          <SelectInput
            label="Account type"
            require
            value={accountType}
            onSelect={(val) => {
              setAccountType(val);
              setAccountTypeError("");
            }}
            options={["Savings account", "Current account"]}
            error={accountTypeError}
          />

          <Input
            require
            label="Account number"
            type="Number"
            placeholder="Enter account number here"
            className="w-100 pe-lg-5"
            value={accountNumber}
            error={accountNumberError}
            // errorMessage={}
            handleChange={setAccountNumber}
            onBlur={validateForm}
            autoFocus={true}
          />

          <Input
            require
            type="text"
            label="IFSC Code"
            placeholder="Enter IFSC here"
            className="w-100 pe-lg-5"
            value={ifsc}
            error={ifscError}
            // errorMessage={ifscError}
            handleChange={setIfsc}
            onBlur={validateForm}
          />

          <Input
            require
            type="text"
            label="Account holder name"
            placeholder="Enter name"
            className="w-100 pe-lg-5"
            value={holderName}
            error={holderNameError}
            // errorMessage={holderNameError}
            handleChange={setHolderName}
            onBlur={validateForm}
          />

          <div className="col-lg-10 offset-lg-2 col-12">
            <div className="row">
              <div className="col-6">
                <PrimaryButton
                  ClassName="e-border-radius-pill e-border-1px-blue py-3 e-font-weight-500 e-font-16 w-100 bg-transparent e-text-blue-violet e-primary-font-poppins"
                  name="Go back"
                  onPress={() => setSelected(null)}
                />
              </div>
              <div className="col-6">
                <PrimaryButton
                  ClassName="e-border-radius-pill e-font-weight-500 py-3 e-font-16 w-100 e-bg-blue-violet text-white e-primary-font-poppins"
                  name="Link bank"
                  onPress={_handleSubmit}
                />
              </div>
            </div>
          </div>
        </div>
      ) : (
        // ✅ UPI Flow remains the same
        <div className="d-flex flex-column row-gap-3">
          <div
            onClick={() => handleSelect("upi")}
            className="e-primary-font-poppins p-3 d-flex flex-column mt-1 e-bg-ghost-white-dark e-border-radius-16 e-border-1px-pale-lavender"
          >
            <div className="d-flex justify-content-between">
              <div className="d-flex flex-column row-gap-1">
                <h6 className="e-font-weight-500 mb-0 e-font-16 e-text-eerie-black d-flex column-gap-2">
                  Verify bank with UPI
                  <span className="d-flex column-gap-1 align-items-center e-font-10 e-text-purple e-font-weight-400">
                    <Icon icon="thunder" size="13px" />
                    10x faster
                  </span>
                </h6>
                <p className="e-secondary-font-Inter e-font-weight-400 e-font-14">
                  ₹1 for verification, instant refund
                </p>
              </div>
              <img
                style={{ height: "21px" }}
                src={selected === "upi" ? type2Select : type2UnSelect}
                alt=""
              />
            </div>
            <div className="d-flex column-gap-2 align-items-center">
              <img
                style={{ width: "24px" }}
                src={
                  require("../../../Assets/Image/amazonpay-square.svg").default
                }
                alt="amazon-pay"
              />
              <img
                style={{ width: "24px" }}
                src={
                  require("../../../Assets/Image/googlepay-square.svg").default
                }
                alt="google-pay"
              />
              <img
                style={{ width: "24px" }}
                src={require("../../../Assets/Image/paytm-square.svg").default}
                alt="paytm"
              />
              <img
                style={{ width: "24px" }}
                src={
                  require("../../../Assets/Image/phonepe-square.svg").default
                }
                alt="phonepe"
              />
              <p className="e-font-10 e-font-weight-400 e-text-gray-x11 m-0 e-primary-font-poppins">
                +5 more
              </p>
            </div>
          </div>

          <div
            onClick={() => handleSelect("manual")}
            className="e-primary-font-poppins p-3 d-flex flex-column mt-1 e-bg-ghost-white-dark e-border-radius-16 e-border-1px-pale-lavender"
          >
            <div className="d-flex justify-content-between">
              <div className="d-flex flex-column row-gap-1">
                <h6 className="e-font-weight-500 mb-0 e-font-16 e-text-eerie-black d-flex column-gap-2">
                  Add bank details manually
                </h6>
                <p className="e-secondary-font-Inter e-font-weight-400 e-font-14 m-0">
                  Provide bank account details
                </p>
              </div>
              <img
                style={{ height: "21px" }}
                src={selected === "manual" ? type2Select : type2UnSelect}
                alt=""
              />
            </div>
          </div>

          {otpModal && (
            <UpiVerifyModal
              value={upiValue}
              error={upiError}
              handleChange={setUpiValue}
              onClick={handleUpiSubmit}
              close={() => {
                setOtpModal(false);
                setSelected(null);
              }}
            />
          )}

          {upiVerified && (
            <SuccessModal
              onClick={() => setUpiVerified(false)}
              close={() => setUpiVerified(false)}
              type={2}
              title={"Bank account verified!"}
              message={
                "₹1 will be debited from the following account and refunded within 48 hours"
              }
            />
          )}
        </div>
      )}
    </>
  );
}
